import { put, call, all, takeLatest, select } from 'redux-saga/effects'
import _ from 'lodash/fp'
import gql from 'graphql-tag'

import * as AssetsReducer from '../reducers/assets'
import * as ClubsReducer from '../reducers/clubs'

import apolloClient from '../apollo'

const CATEGORIES_QUERY = gql`
  query assetCategories {
    assetCategories {
      handle
      description
    }
  }
`

const ASSETS_FOR_CLUB_QUERY = gql`
  query assetsForClub($clubId: ID) {
    assetsForClub(clubId: $clubId) {
      name
      url
      tags
      categoryHandle
      clubId
      elements
    }
  }
`

const UPSERT_ASSET_MUTATION = gql`
  mutation upsertAsset($input: UpsertAssetInput!) {
    upsertAsset(input: $input)
  }
`

const DELETE_ASSET_MUTATION = gql`
  mutation deleteAsset($assetName: String!) {
    deleteAsset(assetName: $assetName)
  }
`

//  ███████╗███████╗███████╗███████╗ ██████╗████████╗███████╗
//  ██╔════╝██╔════╝██╔════╝██╔════╝██╔════╝╚══██╔══╝██╔════╝
//  █████╗  █████╗  █████╗  █████╗  ██║        ██║   ███████╗
//  ██╔══╝  ██╔══╝  ██╔══╝  ██╔══╝  ██║        ██║   ╚════██║
//  ███████╗██║     ██║     ███████╗╚██████╗   ██║   ███████║
//  ╚══════╝╚═╝     ╚═╝     ╚══════╝ ╚═════╝   ╚═╝   ╚══════╝

function * getCategoriesEffect () {
  try {
    const result = yield call(apolloClient.query, { query: CATEGORIES_QUERY, fetchPolicy: 'network-only' })
    return _.get('data.assetCategories', result) || []
  } catch (error) {
    return { error }
  }
}

function * getAssetsEffect (payload) {
  const clubId = _.get('clubId', payload)
  try {
    const result = yield call(apolloClient.query, {
      query: ASSETS_FOR_CLUB_QUERY,
      variables: { clubId },
      fetchPolicy: 'network-only'
    })
    return _.get('data.assetsForClub', result) || []
  } catch (error) {
    return { error }
  }
}

function * postAssetEffect (payload) {
  const bundleName = _.get('bundle.name', payload)
  try {
    const result = yield call(apolloClient.mutate, {
      mutation: UPSERT_ASSET_MUTATION,
      variables: {
        input: {
          name: bundleName,
          elements: _.getOr([], 'elements', payload),
          category: _.get('category.handle', payload) ? { handle: _.get('category.handle', payload) } : undefined,
          clubId: _.get('clubId', payload) || undefined,
          tags: _.getOr([], 'tags', payload)
        }
      }
    })
    return _.get('data.upsertAsset', result)
  } catch (error) {
    return { error }
  }
}

function * deleteAssetEffect (payload) {
  const bundleName = _.get('bundle.name', payload)
  try {
    const result = yield call(apolloClient.mutate, {
      mutation: DELETE_ASSET_MUTATION,
      variables: { assetName: bundleName }
    })
    return _.get('data.deleteAsset', result)
  } catch (error) {
    return { error }
  }
}

//  ███████╗ █████╗  ██████╗  █████╗ ███████╗
//  ██╔════╝██╔══██╗██╔════╝ ██╔══██╗██╔════╝
//  ███████╗███████║██║  ███╗███████║███████╗
//  ╚════██║██╔══██║██║   ██║██╔══██║╚════██║
//  ███████║██║  ██║╚██████╔╝██║  ██║███████║
//  ╚══════╝╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═╝╚══════╝

function * getCategoriesFlow () {
  const result = yield call(getCategoriesEffect)
  const error = _.get('error', result)
  if (error) {
    yield put(AssetsReducer.actions.getCategoriesFailure(error))
  } else {
    yield put(AssetsReducer.actions.getCategoriesSuccess(result))
  }
}

function * getAssetsFlow ({ payload }) {
  const clubId = yield select(ClubsReducer.selectors.getActive)
  const result = yield call(getAssetsEffect, _.set('clubId', clubId, payload))
  const error = _.get('error', result)
  if (error) {
    yield put(AssetsReducer.actions.getAssetsFailure(error))
  } else {
    yield put(AssetsReducer.actions.getAssetsSuccess(result))
  }
}

function * postAssetsFlow ({ payload }) {
  const calls = _.map((bundlePackage) => {
    return call(postAssetEffect, bundlePackage)
  }, payload)
  try {
    yield all(calls)
  } catch (e) {
    return yield put(AssetsReducer.actions.postAssetsFailure(e))
  }
  yield put(AssetsReducer.actions.postAssetsSuccess('success'))
  yield put(AssetsReducer.actions.getAssets())
}

function * deleteAssetFlow ({ payload }) {
  const result = yield call(deleteAssetEffect, payload)
  const error = _.get('error', result)
  if (error) {
    yield put(AssetsReducer.actions.deleteAssetFailure(error))
  } else {
    yield put(AssetsReducer.actions.deleteAssetSuccess('delete sucess'))
    yield put(AssetsReducer.actions.getAssets())
  }
}

export default function * () {
  return yield all([
    takeLatest(AssetsReducer.types.GET_CATEGORIES, getCategoriesFlow),
    takeLatest(AssetsReducer.types.GET_ASSETS, getAssetsFlow),
    takeLatest(AssetsReducer.types.POST_ASSETS, postAssetsFlow),
    takeLatest(AssetsReducer.types.DELETE_ASSET, deleteAssetFlow)
  ])
}
